import React, { useEffect, useState, useCallback } from "react";
import {
  useParams,
  useNavigate,
  Link as RouterLink,
  useSearchParams,
} from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Breadcrumbs,
  Chip,
  Divider,
} from "@mui/material";
import MuiLink from "@mui/material/Link";
import { getTreatmentById, updateTreatment } from "../../api/treatment";
import { useSnackbar } from "notistack";
import useAuth from "../../hooks/useAuth";
import { APP_HOME_PATH } from "../../config/routes";
import MedicationIcon from "@mui/icons-material/Medication";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

const sanitizeUpdate = (form) => {
  const out = {};
  Object.entries(form).forEach(([k, v]) => {
    if (v === "" || v === undefined) return;
    if (v === null) {
      out[k] = null;
      return;
    }
    if (k === "date" || k === "next_dosage") {
      out[k] = String(v).slice(0, 10);
      return;
    }
    if (k === "time") {
      const t = String(v);
      out[k] = t.length === 5 ? `${t}:00` : t;
      return;
    }
    out[k] = v;
  });
  return out;
};

const TreatmentEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const isSuperAdmin = user?.roles?.some(
    (r) => r === "super_admin" || r?.name === "super_admin"
  );
  const tenantQ = searchParams.get("tenant_id");
  const tenantParam =
    isSuperAdmin && tenantQ ? Number(tenantQ) : null;
  const profileQuery =
    tenantParam != null ? `?tenant_id=${tenantParam}` : "";
  const [loading, setLoading] = useState(true);
  const [treatment, setTreatment] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await getTreatmentById(id, tenantParam);
      setTreatment(data);
      setForm({
        treatment_scope: data.treatment_scope ?? "individual",
        animal_id: data.animal_id ?? "",
        group_name: data.group_name ?? "",
        group_size: data.group_size ?? "",
        application_method: data.application_method ?? "",
        treatment_of: data.treatment_of ?? "",
        signs_symptoms: data.signs_symptoms ?? "",
        medicine: data.medicine ?? "",
        brand: data.brand ?? "",
        dosage: data.dosage ?? "",
        temperature: data.temperature ?? "",
        comment: data.comment ?? "",
        prescribed_by: data.prescribed_by ?? "",
        date: data.date ?? "",
        time: data.time ?? "",
        next_dosage: data.next_dosage ?? "",
      });
    } catch (e) {
      setLoadError(e?.message || "Failed to load treatment");
      enqueueSnackbar("Failed to load treatment", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [id, tenantParam, enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      if (
        form.treatment_scope === "individual" &&
        (form.animal_id === "" || form.animal_id == null)
      ) {
        setError("Animal ID is required for individual treatments");
        setSaving(false);
        return;
      }
      if (
        form.treatment_scope === "group" &&
        !String(form.group_name || "").trim()
      ) {
        setError("Group name is required for group treatments");
        setSaving(false);
        return;
      }
      const payload = sanitizeUpdate(form);
      if (form.treatment_scope === "individual") {
        payload.group_name = null;
        payload.group_size = null;
        payload.application_method = null;
        payload.animal_id = Number(form.animal_id);
      } else {
        payload.animal_id = null;
      }
      await updateTreatment(Number(id), payload, tenantParam);
      enqueueSnackbar("Treatment updated", { variant: "success" });
      navigate(`/treatment/${id}${profileQuery}`);
    } catch (e) {
      const msg = e?.detail || e?.message || "Failed to update treatment";
      setError(msg);
      enqueueSnackbar(String(msg), { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError || !treatment) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{loadError || "Treatment not found"}</Alert>
        <Button
          component={RouterLink}
          to={
            tenantParam != null
              ? `/treatments?tenant_id=${tenantParam}`
              : "/treatments"
          }
          sx={{ mt: 2 }}
        >
          Back to list
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100%",
        pb: 4,
        pt: { xs: 2, md: 3 },
        px: { xs: 2, md: 3 },
        bgcolor: "background.default",
      }}
    >
      <Box sx={{ width: "100%", maxWidth: { xs: "100%", md: 1280 }, mx: "auto" }}>
      <Breadcrumbs sx={{ mb: 2, flexWrap: "wrap" }}>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to={APP_HOME_PATH}>
          Dashboard
        </MuiLink>
        <MuiLink
          component={RouterLink}
          underline="hover"
          color="inherit"
          to={
            tenantParam != null
              ? `/treatments?tenant_id=${tenantParam}`
              : "/treatments"
          }
        >
          Treatments
        </MuiLink>
        <MuiLink
          component={RouterLink}
          underline="hover"
          color="inherit"
          to={`/treatment/${id}${profileQuery}`}
        >
          {treatment.treatment_id}
        </MuiLink>
        <Typography color="text.primary">Edit</Typography>
      </Breadcrumbs>

      <Paper
        elevation={0}
        sx={{
          mb: 2.5,
          p: { xs: 2.5, md: 3.5 },
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          boxShadow: 1,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: { xs: 48, sm: 52 },
                height: { xs: 48, sm: 52 },
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                bgcolor: "primary.main",
                color: "primary.contrastText",
                boxShadow: "0 6px 16px rgba(21,101,192,0.28)",
              }}
            >
              <MedicationIcon sx={{ fontSize: { xs: 26, sm: 30 } }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                component="h1"
                sx={{
                  fontWeight: 800,
                  letterSpacing: -0.4,
                  color: "text.primary",
                  lineHeight: 1.2,
                  fontSize: { xs: "1.3rem", sm: "2rem" },
                }}
              >
                Edit treatment
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.75, gap: 0.75 }}>
                <Chip
                  size="small"
                  label={form.treatment_scope === "group" ? "Group / Herd" : "Individual"}
                  sx={{ fontWeight: 700, bgcolor: "rgba(21,101,192,0.12)", color: "primary.dark" }}
                />
                <Chip
                  size="small"
                  label={form.medicine || "Medicine"}
                  sx={{ fontWeight: 700, bgcolor: "rgba(0,137,123,0.12)", color: "teal" }}
                />
              </Stack>
            </Box>
          </Stack>
          <Button
            component={RouterLink}
            to={`/treatment/${id}${profileQuery}`}
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            sx={{ borderRadius: 2, width: { xs: "100%", sm: "auto" } }}
          >
            Cancel
          </Button>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        {treatment.animal_id == null && treatment.treatment_scope !== "group" && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This treatment is no longer linked to an animal (animal was removed).
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {String(error)}
          </Alert>
        )}
        <Box
          sx={{
            display: "grid",
            gap: 2.5,
            gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "rgba(21, 101, 192, 0.24)",
              borderLeft: "5px solid",
              borderLeftColor: "primary.main",
            }}
          >
            <Typography variant="subtitle1" fontWeight={800} sx={{ color: "primary.dark", letterSpacing: 0.2 }}>
              Treatment information
            </Typography>
            <Divider sx={{ my: 2, opacity: 0.85 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Treatment type"
              name="treatment_scope"
              value={form.treatment_scope}
              onChange={handleChange}
              fullWidth
              size="small"
              disabled
            >
              <MenuItem value="individual">Individual</MenuItem>
              <MenuItem value="group">Group / Herd</MenuItem>
            </TextField>
              </Grid>
              {form.treatment_scope === "individual" ? (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Animal tag number"
                    value={treatment.animal_tag_number || "—"}
                    fullWidth
                    size="small"
                    disabled
                  />
                </Grid>
              ) : (
                <>
                  <Grid item xs={12} sm={4}>
                <TextField
                  label="Group name"
                  name="group_name"
                  value={form.group_name}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                <TextField
                  label="Group size"
                  name="group_size"
                  type="number"
                  value={form.group_size}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                <TextField
                  label="Application method"
                  name="application_method"
                  value={form.application_method}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                />
                  </Grid>
                </>
              )}
              <Grid item xs={12} sm={6}>
            <TextField
              label="Condition"
              name="treatment_of"
              value={form.treatment_of}
              onChange={handleChange}
              fullWidth
              size="small"
            />
              </Grid>
              <Grid item xs={12} sm={6}>
            <TextField
              label="Signs/Symptoms"
              name="signs_symptoms"
              value={form.signs_symptoms}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>
              <Grid item xs={12} sm={4}>
            <TextField
              type="date"
              label="Date"
              name="date"
              value={form.date}
              onChange={handleChange}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
              </Grid>
              <Grid item xs={12} sm={4}>
            <TextField
              type="time"
              label="Time"
              name="time"
              value={form.time?.slice(0, 5) ?? ""}
              onChange={handleChange}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
              </Grid>
              <Grid item xs={12} sm={4}>
            <TextField
              type="date"
              label="Next dosage"
              name="next_dosage"
              value={form.next_dosage}
              onChange={handleChange}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
              </Grid>
            </Grid>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "rgba(0, 137, 123, 0.26)",
              borderLeft: "5px solid",
              borderLeftColor: "teal",
            }}
          >
            <Typography variant="subtitle1" fontWeight={800} sx={{ color: "teal", letterSpacing: 0.2 }}>
              Medication details
            </Typography>
            <Divider sx={{ my: 2, opacity: 0.85 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Medicine"
                  name="medicine"
                  value={form.medicine}
                  fullWidth
                  size="small"
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Brand"
                  name="brand"
                  value={form.brand}
                  fullWidth
                  size="small"
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Dosage"
                  name="dosage"
                  value={form.dosage}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Temperature"
                  name="temperature"
                  value={form.temperature}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Prescribed by"
                  name="prescribed_by"
                  value={form.prescribed_by}
                  fullWidth
                  size="small"
                  disabled
                />
              </Grid>
            </Grid>
          </Paper>
        </Box>

        <Paper
          elevation={0}
          sx={{
            mt: 2.5,
            p: 2.5,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "rgba(2, 136, 209, 0.22)",
            borderLeft: "5px solid",
            borderLeftColor: "info.main",
          }}
        >
          <Typography variant="subtitle1" fontWeight={800} sx={{ color: "info.dark", letterSpacing: 0.2 }}>
            Clinical notes
          </Typography>
          <Divider sx={{ my: 2, opacity: 0.85 }} />
          <Grid container spacing={2}>
            <Grid item xs={12}>
            <TextField
              label="Comments"
              name="comment"
              value={form.comment}
              onChange={handleChange}
              fullWidth
              multiline
              minRows={4}
              size="small"
            />
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  mt: 1,
                  pt: 2,
                  borderTop: 1,
                  borderColor: "divider",
                }}
              >
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              startIcon={<SaveOutlinedIcon />}
              sx={{ borderRadius: 2, boxShadow: "0 4px 12px rgba(21,101,192,0.24)" }}
            >
              {saving ? "Saving…" : "Save changes"}
            </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Paper>
      </Box>
    </Box>
  );
};

export default TreatmentEdit;
